import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { User, useUpdateUserMutation } from 'entities/User';
import { useCallback, useState } from 'react';
import { getServerDateFormat } from 'shared/utils/helpers/getDateFormat';
import { isErrorApiResponse } from 'shared/utils/helpers/isErrorApiResponse';
import { transformEmptyStringToNull } from 'shared/utils/helpers/transformEmptyStringToNull';

interface HookArgs {
  userId: string | undefined;
  updatedData: Nullable<Partial<User>>;
  onSuccess?: () => void;
}

interface HookApi {
  isOpenedConfirm: boolean;
  isLoading: boolean;
  handleUpdateUser: () => void;
  openConfirmModal: () => void;
  closeConfirmModal: () => void;
}

export const useUpdateUser = ({ userId, updatedData, onSuccess }: HookArgs): HookApi => {
  const [isOpenedConfirm, setOpenedConfirm] = useState(false);

  const { t } = useAppTranslation('common');

  const [updateUser, { isLoading }] = useUpdateUserMutation();

  const openConfirmModal = useCallback((): void => {
    setOpenedConfirm(true);
  }, []);

  const closeConfirmModal = useCallback((): void => {
    setOpenedConfirm(false);
  }, []);

  const handleUpdateUser = useCallback(async (): Promise<void> => {
    if (userId && updatedData) {
      try {
        const params = transformEmptyStringToNull(updatedData);

        if (updatedData.dateOfBirth) {
          params.dateOfBirth = getServerDateFormat(updatedData.dateOfBirth);
        }

        await updateUser({
          userId,
          params,
        }).unwrap();
        showNotification('info', t('Success'), t('User has been successfully updated'));
        onSuccess?.();
      } catch (error) {
        if (isErrorApiResponse(error)) {
          const errorMessage = Array.isArray(error.data.message)
            ? error.data.message.map((message) => t(message, { ns: 'common' }))
            : t(error.data.message, { ns: 'common' });
          showNotification('error', t('Error', { ns: 'common' }), errorMessage);
        } else {
          console.log(error);
        }
      } finally {
        closeConfirmModal();
      }
    }
  }, [userId, updateUser, updatedData, t, onSuccess, closeConfirmModal]);

  return {
    openConfirmModal,
    closeConfirmModal,
    handleUpdateUser,
    isOpenedConfirm,
    isLoading,
  };
};
